import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { SegmentedControl } from '@ds/navigation';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { SITUACAO_DA_NOTA, documentoFormatado, situacaoDoCertificado } from '@/data/notasFiscais';
import type { AlvoFiscal, EmitenteFiscal, NotaFiscal } from '@/data/notasFiscais';
import { ConfiguracaoFiscalModal } from './ConfiguracaoFiscalModal';
import { NotaFiscalModal } from './NotaFiscalModal';

export interface FaturaParaPreparar {
    id: string;
    clinica: string;
    competencia: string;
    valorCentavos: number;
}

export interface PainelDeNotasFiscaisProps {
    alvo: AlvoFiscal;
    /** Na plataforma: as faturas pagas, para preparar a nota de cada uma. */
    faturasPagas?: FaturaParaPreparar[];
}

/**
 * Notas fiscais — a mesma tela para as duas pontas: dados fiscais, certificado,
 * ligação com o emissor, rascunho, pendências, emissão e cancelamento. Enquanto
 * alguma nota espera a prefeitura, a lista se relê sozinha a cada 15 segundos.
 */
export function PainelDeNotasFiscais({ alvo, faturasPagas = [] }: PainelDeNotasFiscaisProps) {
    const aviso = useAviso();
    const [params, setParams] = useSearchParams();
    const [emitente, setEmitente] = useState<EmitenteFiscal | null | undefined>(undefined);
    const [notas, setNotas] = useState<NotaFiscal[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [configurando, setConfigurando] = useState(false);
    /* Na clínica, também as notas que a plataforma emitiu PARA ela. */
    const [origem, setOrigem] = useState<AlvoFiscal>(alvo);
    const [preparando, setPreparando] = useState<string | null>(null);

    const carregar = useCallback(async () => {
        try {
            const [e, n] = await Promise.all([dados().emitenteFiscal(alvo), dados().notasFiscais({ origem })]);
            setEmitente(e);
            setNotas(n);
            setErro(null);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler as notas fiscais.');
        }
    }, [alvo, origem]);

    useEffect(() => { void carregar(); }, [carregar]);

    const esperando = (notas ?? []).some((n) => n.situacao === 'na_fila' || n.situacao === 'processando' || n.situacao === 'cancelando');
    useEffect(() => {
        if (!esperando) return undefined;
        const relogio = window.setInterval(() => { void carregar(); }, 15_000);
        return () => window.clearInterval(relogio);
    }, [esperando, carregar]);

    const notaAberta = params.get('nota');
    const abrirNota = (id: string | null) => setParams((atuais) => {
        const proximos = new URLSearchParams(atuais);
        if (id) proximos.set('nota', id);
        else proximos.delete('nota');
        return proximos;
    });
    const aberta = notas?.find((n) => n.id === notaAberta) ?? null;

    const prepararDaFatura = async (faturaId: string) => {
        setPreparando(faturaId);
        try {
            const { id } = await dados().prepararNotaDaFatura(faturaId);
            await carregar();
            abrirNota(id);
            aviso({ title: 'Rascunho da nota preparado' });
        } catch (e) {
            aviso({ title: 'Não foi possível preparar a nota', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setPreparando(null);
        }
    };

    const emissorLigado = Boolean(emitente?.provedor && emitente.emissor?.ligadoEm);
    const cert = situacaoDoCertificado(emitente?.certificado ?? null);
    const rascunhos = (notas ?? []).filter((n) => n.situacao === 'rascunho');
    const prontas = rascunhos.filter((n) => n.pendencias.length === 0);
    const autorizadas = (notas ?? []).filter((n) => n.situacao === 'autorizada');
    const comNota = new Set((notas ?? []).filter((n) => n.situacao !== 'cancelada' && n.situacao !== 'rejeitada').map((n) => n.faturaId));
    const faturasSemNota = faturasPagas.filter((f) => !comNota.has(f.id));
    const recebidas = origem !== alvo;

    const colunas: DataTableColumn<NotaFiscal>[] = [
        { key: 'criadoEm', header: 'Preparada', render: (n) => dataSimples(n.criadoEm.slice(0, 10)) },
        {
            key: 'tomadorNome', header: alvo === 'plataforma' ? 'Clínica' : recebidas ? 'Emitente' : 'Paciente',
            render: (n) => (recebidas ? 'Fórmula MED' : (
                <span>
                    {n.tomadorNome ?? '—'}
                    {n.tomadorDocumento && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{documentoFormatado(n.tomadorDocumento)}</span>}
                </span>
            )),
        },
        { key: 'descricao', header: 'Serviço', render: (n) => <span style={{ display: 'block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.descricao}</span> },
        { key: 'valorServicosCentavos', header: 'Valor', align: 'right', render: (n) => moeda(n.valorServicosCentavos) },
        { key: 'valorIssCentavos', header: 'ISS', align: 'right', render: (n) => moeda(n.valorIssCentavos) },
        {
            key: 'situacao', header: 'Situação',
            render: (n) => (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Badge tone={SITUACAO_DA_NOTA[n.situacao].tom} dot>{SITUACAO_DA_NOTA[n.situacao].rotulo}</Badge>
                    {n.situacao === 'rascunho' && !recebidas && (
                        n.pendencias.length
                            ? <Badge tone="warning">{n.pendencias.length} {n.pendencias.length === 1 ? 'pendência' : 'pendências'}</Badge>
                            : <Badge tone="success">pronta</Badge>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {erro && <Alert tone="danger" title="Não foi possível ler as notas fiscais">{erro}</Alert>}

            {emitente !== undefined && !emissorLigado && (
                <Alert tone="info" title="A empresa ainda não está ligada ao emissor" dismissible={false}>
                    Preencha os dados fiscais e envie o certificado; depois, em Dados fiscais, use "Ligar ao emissor".
                    Até lá dá para preparar as notas — cada uma mostra o que falta —, mas nenhuma é enviada à prefeitura.
                </Alert>
            )}

            <Card padding={18}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <Icon name="landmark" size={18} style={{ color: 'var(--text-secondary)' }} />
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <strong style={{ fontSize: 14 }}>
                            {emitente?.razaoSocial ?? (alvo === 'plataforma' ? 'Dados fiscais da plataforma' : 'Dados fiscais da clínica')}
                        </strong>
                        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            {emitente?.cnpj ? `CNPJ ${documentoFormatado(emitente.cnpj)}` : 'Ainda não configurados'}
                            {emitente?.municipio ? ` · ${emitente.municipio}/${emitente.uf ?? ''}` : ''}
                            {emitente ? ` · ${emitente.ambiente === 'producao' ? 'produção' : 'homologação'}` : ''}
                            {emissorLigado ? ` · emissor ${emitente?.emissor?.padrao === 'nacional' ? 'no padrão nacional' : 'no sistema da prefeitura'}` : ''}
                        </span>
                    </div>
                    <Badge dot tone={cert.tipo === 'ausente' ? 'neutral' : cert.tipo === 'vencido' ? 'danger' : cert.tipo === 'vencendo' ? 'warning' : 'success'}>
                        {cert.tipo === 'ausente' ? 'Sem certificado'
                            : cert.tipo === 'vencido' ? 'Certificado vencido'
                                : cert.tipo === 'vencendo' ? `Certificado vence em ${cert.dias} ${cert.dias === 1 ? 'dia' : 'dias'}`
                                    : `Certificado até ${dataSimples(cert.ate.slice(0, 10))}`}
                    </Badge>
                    <Button variant="secondary" iconLeft={<Icon name="settings" size={15} />} onClick={() => setConfigurando(true)} disabled={emitente === undefined}>
                        Dados fiscais
                    </Button>
                </div>
            </Card>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard label="Rascunhos" value={String(rascunhos.length)} hint="preparados, não enviados" icon={<Icon name="file-pen" size={16} />} />
                <KpiCard label="Prontas" value={String(prontas.length)} hint={emissorLigado ? 'sem pendência' : 'falta ligar o emissor'} icon={<Icon name="file-check" size={16} />} />
                <KpiCard label="Com pendência" value={String(rascunhos.length - prontas.length)} hint="falta dado para emitir" deltaTone={rascunhos.length - prontas.length ? 'down' : 'neutral'} icon={<Icon name="triangle-alert" size={16} />} />
                <KpiCard label="Autorizadas" value={String(autorizadas.length)} hint="pela prefeitura" icon={<Icon name="badge-check" size={16} />} />
            </div>

            {alvo === 'clinica' && (
                <SegmentedControl
                    value={origem} onChange={(v) => setOrigem(v as AlvoFiscal)} ariaLabel="Quais notas"
                    options={[{ value: 'clinica', label: 'Emitidas pela clínica' }, { value: 'plataforma', label: 'Recebidas da Fórmula MED' }]}
                />
            )}

            {alvo === 'clinica' && origem === 'clinica' && (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    A nota nasce do título a receber: abra o título (Contas a receber) e use "Preparar nota fiscal".
                </p>
            )}

            {alvo === 'plataforma' && faturasSemNota.length > 0 && (
                <Card title="Faturas pagas sem nota" subtitle="A plataforma emite; a clínica é a tomadora" padding={0}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {faturasSemNota.map((f, i) => (
                            <li key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 18px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)', fontSize: 13 }}>
                                <span style={{ flex: 1, minWidth: 180 }}><strong>{f.clinica}</strong> · {f.competencia}</span>
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{moeda(f.valorCentavos)}</span>
                                <Button size="sm" variant="secondary" onClick={() => { void prepararDaFatura(f.id); }} loading={preparando === f.id}>
                                    Preparar nota
                                </Button>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            <Card padding={0}>
                <DataTable
                    columns={colunas} rows={notas ?? []} rowKey="id"
                    onRowClick={(n) => abrirNota(n.id)}
                    rowLabel={(n) => `Nota de ${n.tomadorNome ?? 'tomador'} · ${moeda(n.valorServicosCentavos)}`}
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage={notas === null ? 'Carregando…' : recebidas ? 'Nenhuma nota recebida da Fórmula MED.' : 'Nenhuma nota preparada ainda.'}
                />
            </Card>

            {configurando && (
                <ConfiguracaoFiscalModal
                    alvo={alvo} emitente={emitente ?? null}
                    onFechar={() => { setConfigurando(false); void carregar(); }}
                    onMudou={(e) => setEmitente(e)}
                />
            )}
            {aberta && (
                <NotaFiscalModal
                    key={aberta.id} nota={aberta} emissorLigado={emissorLigado} somenteLeitura={recebidas}
                    onFechar={() => abrirNota(null)}
                    onMudou={() => { void carregar(); }}
                />
            )}
        </div>
    );
}
